import React, { useState, useEffect } from "react";
import BulkSendForm from "../components/BulkSendForm";
import BulkSendRequests from "../components/BulkSendRequests";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { usePageHeader } from "../contexts/PageHeaderContext";
import BreadCrumb from "../components/common/BreadCrumb";
import PermissionGuard from "../components/common/PermissionGuard";
import PagePermissionGuard from "../components/common/PagePermissionGuard";
import { Button, Card } from "../components/ui";
import { PlusIcon, ListBulletIcon } from "@heroicons/react/24/outline";

const SUB_TABS = {
  create: "create",
  requests: "requests",
};

const BulkSend = () => {
  const { organization } = useAuth();
  const { setHeaderContent } = usePageHeader();
  const [activeSubTab, setActiveSubTab] = useState(SUB_TABS.create);
  const navigate = useNavigate();
  // console.log(user);

  // Check if bulk send is allowed
  const isBulkSendAllowed = organization?.planMeta?.["BULK_SEND"] === true;

  // Set page header for Navbar (only when feature is available)
  useEffect(() => {
    if (!isBulkSendAllowed) return;
    setHeaderContent(
      <div>
        <BreadCrumb
          items={[
            { label: "Dashboard", href: "/dashboard" },
            { label: "Bulk Send" },
          ]}
          className="mb-2"
        />
        <h1 className="font-heading text-xl sm:text-2xl font-bold text-gray-900">
          Bulk Send
        </h1>
        <p className="text-sm text-gray-600 max-w-2xl mt-1">
          Manage your bulk send requests and view details
        </p>
      </div>,
    );
    return () => setHeaderContent(null);
  }, [isBulkSendAllowed, setHeaderContent]);

  // Feature not available UI
  if (!isBulkSendAllowed) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center" padding="lg">
          {/* Icon */}
          <div className="mx-auto w-16 h-16 bg-primary rounded-full flex items-center justify-center mb-6">
            <svg
              className="w-8 h-8 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>

          {/* Title */}
          <h1 className="font-heading text-xl font-bold text-gray-900 mb-3">
            Feature Not Available
          </h1>

          {/* Description */}
          <p className="text-gray-600 mb-6 leading-relaxed">
            Bulk Send is a premium feature. Upgrade your plan to unlock this
            powerful feature and send documents to multiple recipients
            efficiently.
          </p>

          {/* CTA Button */}
          <Button
            onClick={() => navigate("/dashboard/organization/subscription")}
            fullWidth
            size="lg"
          >
            Upgrade plan
          </Button>
        </Card>
      </div>
    );
  }

  const sidebarLinks = [
    { value: SUB_TABS.create, label: "Create Bulk Send", icon: PlusIcon },
    {
      value: SUB_TABS.requests,
      label: "Bulk Send Requests",
      icon: ListBulletIcon,
    },
  ];

  return (
    <PagePermissionGuard module="bulk_send" action="read" pageName="Bulk Send">
      <div className="flex flex-col lg:flex-row gap-6 h-screen">
        {/* ── Vertical sidebar: Navigation links ── */}
        <aside className="w-full lg:w-60 shrink-0 lg:border-r px-4 py-4 border-gray-200 lg:rounded-none h-screen">
          <nav className="space-y-1">
            {sidebarLinks.map((link) => {
              const Icon = link.icon;
              const isActive = activeSubTab === link.value;
              return (
                <button
                  key={link.value}
                  type="button"
                  onClick={() => setActiveSubTab(link.value)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-primary/10 text-primary"
                      : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                  }`}
                >
                  <Icon className="w-5 h-5 shrink-0" />
                  {link.label}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* ── Main content ── */}
        <div className="flex-1 min-w-0 p-4 overflow-auto">
          {activeSubTab === SUB_TABS.create && (
            <PermissionGuard
              module="bulk_send"
              action="write"
              showMessage={true}
              message="You are not allowed to create bulk send requests"
            >
              <BulkSendForm />
            </PermissionGuard>
          )}
          {activeSubTab === SUB_TABS.requests && (
            <PermissionGuard
              module="bulk_send"
              action="read"
              showMessage={true}
              message="You are not allowed to view bulk send requests"
            >
              <BulkSendRequests />
            </PermissionGuard>
          )}
        </div>
      </div>
    </PagePermissionGuard>
  );
};

export default BulkSend;
